import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, Download, AlertCircle, ShieldCheck, RotateCcw, Eye, EyeOff,
  MapPin, Camera, Calendar, FileText, Settings, Copyright, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Info, Plus, Trash2
} from 'lucide-react';
import { formatFileSize } from '../config/api';

interface MetadataMarker {
  name: string;
  category: string;
  found: boolean;
  icon: React.ReactNode;
  privacyRisk: 'high' | 'medium' | 'low';
  description: string;
}

interface FileEntry {
  id: string;
  file: File;
  preview: string;
  markers: MetadataMarker[];
  privacyLevel: 'high' | 'medium' | 'low';
  resultUrl: string | null;
  resultSize: number;
  status: 'pending' | 'scanning' | 'scanned' | 'processing' | 'done' | 'error';
  error: string | null;
}

const RemoveExif: React.FC = () => {
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [outputFormat, setOutputFormat] = useState<'original' | 'png'>('original');
  const [quality, setQuality] = useState(95);
  const [showMetadata, setShowMetadata] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const scanExifMarkers = async (file: File): Promise<MetadataMarker[]> => {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const markers: MetadataMarker[] = [];

    const hasBytes = (offset: number, target: number[]): boolean => {
      for (let i = 0; i < target.length; i++) {
        if (offset + i >= bytes.length) return false;
        if (bytes[offset + i] !== target[i]) return false;
      }
      return true;
    };

    const containsString = (str: string): boolean => {
      const encoder = new TextEncoder();
      const encoded = encoder.encode(str);
      for (let i = 0; i < bytes.length - encoded.length; i++) {
        let found = true;
        for (let j = 0; j < encoded.length; j++) {
          if (bytes[i + j] !== encoded[j]) { found = false; break; }
        }
        if (found) return true;
      }
      return false;
    };

    // Check for EXIF header (APP1 marker 0xFFE1 with "Exif")
    let hasExif = false;
    for (let i = 0; i < Math.min(bytes.length - 6, 65536); i++) {
      if (bytes[i] === 0xFF && bytes[i + 1] === 0xE1) {
        if (hasBytes(i + 4, [0x45, 0x78, 0x69, 0x66])) {
          hasExif = true;
          break;
        }
      }
    }

    // Check for GPS data within EXIF
    const hasGPS = containsString('GPS');

    // Check for Photoshop/IPTC (APP13 marker 0xFFED)
    let hasIPTC = false;
    for (let i = 0; i < Math.min(bytes.length - 2, 65536); i++) {
      if (bytes[i] === 0xFF && bytes[i + 1] === 0xED) {
        hasIPTC = true;
        break;
      }
    }

    // Check for XMP data
    const hasXMP = containsString('http://ns.adobe.com/xap') || containsString('adobe:ns:meta');

    // Check for ICC profile (APP2 marker 0xFFE2)
    let hasICC = false;
    for (let i = 0; i < Math.min(bytes.length - 2, 65536); i++) {
      if (bytes[i] === 0xFF && bytes[i + 1] === 0xE2) {
        hasICC = true;
        break;
      }
    }

    // Check for common camera makers
    const hasCameraInfo = containsString('Canon') || containsString('Nikon') ||
      containsString('Sony') || containsString('Apple') || containsString('Samsung') ||
      containsString('OLYMPUS') || containsString('Panasonic') || containsString('FUJIFILM');

    // Check for date strings
    const hasDate = containsString('DateTime') || containsString('DateTimeOriginal');

    // Check for software info
    const hasSoftware = containsString('Software') || containsString('Adobe') ||
      containsString('Photoshop') || containsString('Lightroom') || containsString('GIMP');

    // Check for copyright
    const hasCopyright = containsString('Copyright') || containsString('copyright');

    markers.push({
      name: 'GPS Location',
      category: 'GPS Data',
      found: hasGPS,
      icon: <MapPin className="w-4 h-4" />,
      privacyRisk: 'high',
      description: 'Geographic coordinates where the photo was taken',
    });

    markers.push({
      name: 'Camera Information',
      category: 'Camera Info',
      found: hasCameraInfo || hasExif,
      icon: <Camera className="w-4 h-4" />,
      privacyRisk: 'medium',
      description: 'Camera make, model, lens, and shooting settings',
    });

    markers.push({
      name: 'Date/Time',
      category: 'Date/Time',
      found: hasDate,
      icon: <Calendar className="w-4 h-4" />,
      privacyRisk: 'medium',
      description: 'When the photo was taken or last modified',
    });

    markers.push({
      name: 'Software Info',
      category: 'Software',
      found: hasSoftware,
      icon: <Settings className="w-4 h-4" />,
      privacyRisk: 'low',
      description: 'Editing software used to process the image',
    });

    markers.push({
      name: 'Copyright/Author',
      category: 'Copyright',
      found: hasCopyright,
      icon: <Copyright className="w-4 h-4" />,
      privacyRisk: 'low',
      description: 'Copyright notice and author information',
    });

    markers.push({
      name: 'IPTC Data',
      category: 'Image Settings',
      found: hasIPTC,
      icon: <FileText className="w-4 h-4" />,
      privacyRisk: 'medium',
      description: 'Captions, keywords, and editorial metadata',
    });

    markers.push({
      name: 'XMP Metadata',
      category: 'Image Settings',
      found: hasXMP,
      icon: <FileText className="w-4 h-4" />,
      privacyRisk: 'medium',
      description: 'Extensible editing history and properties',
    });

    markers.push({
      name: 'ICC Color Profile',
      category: 'Image Settings',
      found: hasICC,
      icon: <Settings className="w-4 h-4" />,
      privacyRisk: 'low',
      description: 'Color space and display profile information',
    });

    return markers;
  };

  const getPrivacyLevel = (markers: MetadataMarker[]): 'high' | 'medium' | 'low' => {
    const found = markers.filter(m => m.found);
    if (found.some(m => m.privacyRisk === 'high')) return 'high';
    if (found.some(m => m.privacyRisk === 'medium')) return 'medium';
    if (found.length > 0) return 'low';
    return 'low';
  };

  const handleFileSelect = useCallback(async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setError(null);

    const newEntries: FileEntry[] = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const f = selectedFiles[i];
      if (!f.type.startsWith('image/')) continue;

      const id = `${Date.now()}-${i}`;
      const preview = URL.createObjectURL(f);
      newEntries.push({
        id, file: f, preview,
        markers: [], privacyLevel: 'low',
        resultUrl: null, resultSize: 0,
        status: 'scanning', error: null,
      });
    }

    if (newEntries.length === 0) {
      setError('Please select valid image files.');
      return;
    }

    setFiles(prev => [...prev, ...newEntries]);

    // Scan each file for EXIF markers
    for (const entry of newEntries) {
      try {
        const markers = await scanExifMarkers(entry.file);
        const privacyLevel = getPrivacyLevel(markers);
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, markers, privacyLevel, status: 'scanned' } : f
        ));
      } catch {
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: 'scanned' } : f
        ));
      }
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const removeExifFromFile = useCallback((entry: FileEntry) => {
    setFiles(prev => prev.map(f =>
      f.id === entry.id ? { ...f, status: 'processing' } : f
    ));

    const img = new window.Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);

      const isJpeg = entry.file.type === 'image/jpeg' || entry.file.type === 'image/jpg';
      let mimeType: string;
      let q: number | undefined;

      if (outputFormat === 'png') {
        mimeType = 'image/png';
        q = undefined;
      } else {
        mimeType = isJpeg ? 'image/jpeg' : 'image/png';
        q = isJpeg ? quality / 100 : undefined;
      }

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const resultUrl = URL.createObjectURL(blob);
            setFiles(prev => prev.map(f =>
              f.id === entry.id ? { ...f, resultUrl, resultSize: blob.size, status: 'done' } : f
            ));
          } else {
            setFiles(prev => prev.map(f =>
              f.id === entry.id ? { ...f, status: 'error', error: 'Failed to process image' } : f
            ));
          }
        },
        mimeType,
        q
      );
    };
    img.onerror = () => {
      setFiles(prev => prev.map(f =>
        f.id === entry.id ? { ...f, status: 'error', error: 'Failed to load image' } : f
      ));
    };
    img.src = entry.preview;
  }, [outputFormat, quality]);

  const processAll = useCallback(() => {
    const toProcess = files.filter(f => f.status === 'scanned');
    toProcess.forEach(entry => removeExifFromFile(entry));
  }, [files, removeExifFromFile]);

  const downloadFile = (entry: FileEntry) => {
    if (!entry.resultUrl) return;
    const link = document.createElement('a');
    link.href = entry.resultUrl;
    const name = entry.file.name.replace(/\.[^.]+$/, '');
    const ext = outputFormat === 'png' ? 'png' :
      (entry.file.type === 'image/jpeg' || entry.file.type === 'image/jpg') ? 'jpg' : 'png';
    link.download = `${name}_clean.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    const done = files.filter(f => f.status === 'done');
    done.forEach(entry => downloadFile(entry));
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const entry = prev.find(f => f.id === id);
      if (entry) {
        URL.revokeObjectURL(entry.preview);
        if (entry.resultUrl) URL.revokeObjectURL(entry.resultUrl);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const reset = () => {
    files.forEach(f => {
      URL.revokeObjectURL(f.preview);
      if (f.resultUrl) URL.revokeObjectURL(f.resultUrl);
    });
    setFiles([]);
    setError(null);
    setShowMetadata(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const privacyBadge = (level: 'high' | 'medium' | 'low') => {
    const config = {
      high: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', icon: <XCircle className="w-4 h-4" />, label: 'High Risk' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200', icon: <AlertTriangle className="w-4 h-4" />, label: 'Medium Risk' },
      low: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', icon: <CheckCircle2 className="w-4 h-4" />, label: 'Low Risk' },
    };
    const c = config[level];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
        {c.icon} {c.label}
      </span>
    );
  };

  const doneCount = files.filter(f => f.status === 'done').length;
  const scannedCount = files.filter(f => f.status === 'scanned').length;

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-red-100 rounded-full">
            <ShieldCheck className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Remove EXIF Data</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Scan and strip metadata (EXIF, GPS, camera info, XMP) from your images to protect your privacy. All processing happens in your browser.
        </p>
      </div>

      {/* Upload */}
      {files.length === 0 ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-red-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop images here or click to select
          </h3>
          <p className="text-gray-500 text-sm">Supports JPEG, PNG, WebP. Select multiple files for batch processing.</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Options bar */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Output Format</label>
                <div className="flex gap-2">
                  {([['original', 'Keep Original'], ['png', 'Convert to PNG']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setOutputFormat(val)}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        outputFormat === val
                          ? 'bg-red-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:border-red-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {outputFormat === 'original' && (
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">JPEG Quality</label>
                    <span className="text-lg font-bold text-red-600">{quality}%</span>
                  </div>
                  <input
                    type="range" min="1" max="100" value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">Only applies to JPEG images</p>
                </div>
              )}
            </div>
          </div>

          {/* File list */}
          <div className="space-y-3">
            {files.map(entry => (
              <div key={entry.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  {/* Thumbnail */}
                  <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    <img src={entry.preview} alt="" className="w-full h-full object-cover" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{entry.file.name}</p>
                      {entry.status === 'scanned' && privacyBadge(entry.privacyLevel)}
                      {entry.status === 'done' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle2 className="w-3 h-3" /> Cleaned
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{formatFileSize(entry.file.size)}</span>
                      <span>{entry.file.type.split('/')[1]?.toUpperCase()}</span>
                      {entry.status === 'done' && entry.resultSize > 0 && (
                        <span className="text-green-600 font-medium">
                          Clean: {formatFileSize(entry.resultSize)}
                          {entry.resultSize < entry.file.size
                            ? ` (-${formatFileSize(entry.file.size - entry.resultSize)})`
                            : ` (+${formatFileSize(entry.resultSize - entry.file.size)})`}
                        </span>
                      )}
                      {entry.status === 'scanning' && (
                        <span className="text-blue-600 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Scanning...
                        </span>
                      )}
                      {entry.status === 'processing' && (
                        <span className="text-blue-600 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Removing metadata...
                        </span>
                      )}
                    </div>
                    {/* Privacy report line */}
                    {entry.status === 'scanned' && entry.markers.filter(m => m.found).length > 0 && (
                      <p className="text-xs text-amber-700 mt-1">
                        Contains: {entry.markers.filter(m => m.found).map(m => m.name).join(', ')}
                      </p>
                    )}
                    {entry.status === 'scanned' && entry.markers.filter(m => m.found).length === 0 && (
                      <p className="text-xs text-green-600 mt-1">No significant metadata detected</p>
                    )}
                    {entry.error && (
                      <p className="text-xs text-red-600 mt-1">{entry.error}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {entry.status === 'scanned' && entry.markers.length > 0 && (
                      <button
                        onClick={() => setShowMetadata(showMetadata === entry.id ? null : entry.id)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                        title="View metadata details"
                      >
                        {showMetadata === entry.id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                    {entry.status === 'done' && (
                      <button
                        onClick={() => downloadFile(entry)}
                        className="p-2 rounded-lg hover:bg-green-100 text-green-600 transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => removeFile(entry.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded metadata table */}
                {showMetadata === entry.id && entry.markers.length > 0 && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Metadata Scan Results</h4>
                    <div className="grid gap-2">
                      {entry.markers.map((marker, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center gap-3 p-2.5 rounded-lg text-sm ${
                            marker.found
                              ? marker.privacyRisk === 'high' ? 'bg-red-50' : marker.privacyRisk === 'medium' ? 'bg-yellow-50' : 'bg-gray-50'
                              : 'bg-white opacity-50'
                          }`}
                        >
                          <div className={`flex-shrink-0 ${
                            marker.found
                              ? marker.privacyRisk === 'high' ? 'text-red-500' : marker.privacyRisk === 'medium' ? 'text-yellow-600' : 'text-gray-500'
                              : 'text-gray-300'
                          }`}>
                            {marker.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-800">{marker.name}</span>
                            <span className="text-xs text-gray-500 ml-2">{marker.description}</span>
                          </div>
                          <div className="flex-shrink-0">
                            {marker.found ? (
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                marker.privacyRisk === 'high' ? 'bg-red-100 text-red-700' :
                                marker.privacyRisk === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                Detected
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Not found</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add more files */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-red-300 hover:text-red-600 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add more images
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => { handleFileSelect(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ''; }}
            className="hidden"
          />

          {/* Info */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2 text-xs text-blue-700">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Images are re-drawn on a canvas to strip all metadata. JPEG images stay JPEG (unless you choose PNG). Processing is 100% client-side.
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            {scannedCount > 0 && (
              <button
                onClick={processAll}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
              >
                <ShieldCheck className="w-5 h-5" />
                Remove Metadata ({scannedCount} {scannedCount === 1 ? 'file' : 'files'})
              </button>
            )}
            {doneCount > 1 && (
              <button
                onClick={downloadAll}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                Download All ({doneCount})
              </button>
            )}
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
    </div>
  );
};

export default RemoveExif;
